import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../core/service/types/FormMutation";
import type { FormQuery } from "../../../../../core/service/types/FormQuery";
import type { Dto } from "../../../../../core/types/be/Dto";
import type { ProductProfileEntity } from "../../types/ProductProfileEntity";
import type { ProductProfileProductEntity } from "../../types/subTypes/ProductProfileProductEntity";
import { ProductProfileDtoToEntity } from "../mappers/ProductProfileDtoToEntity";
import { ProductProfileEntityToDto } from "../mappers/ProductProfileEntityToDto";

const ProductProfileApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        ProductProfileLoad: builder.query<ProductProfileEntity, FormQuery>({
            providesTags: [ResourceName.PRODUCT_PROFILES],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.PRODUCT_PROFILES}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return ProductProfileDtoToEntity(response);
            },
        }),
        ProductProfileProductsLoad: builder.query<ProductProfileProductEntity[], { productProfileId?: string } | void>({
            providesTags: [ResourceName.PRODUCT_PROFILES],
            query: (params) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.PRODUCT_PROFILES}/${ResourceName.PRODUCTS}`,
                    method: "GET",
                    params: params?.productProfileId ? { productProfileId: params.productProfileId } : undefined,
                };
            },
            transformResponse: (response: { items: ProductProfileProductEntity[] }) => {
                return response.items;
            },
        }),
        ProductProfileSave: builder.mutation<ProductProfileEntity, FormMutation<ProductProfileEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.PRODUCT_PROFILES}`;
                const method = "POST";

                const body = ProductProfileEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.PRODUCT_PROFILES],
        }),
        ProductProfileUpdate: builder.mutation<ProductProfileEntity, FormMutation<ProductProfileEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.PRODUCT_PROFILES}/${mutation.data.id}`;
                const method = "PUT";

                const body = ProductProfileEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.PRODUCT_PROFILES],
        }),
    }),
});

export const {
    useProductProfileLoadQuery,
    useProductProfileProductsLoadQuery,
    useProductProfileSaveMutation,
    useProductProfileUpdateMutation,
} = ProductProfileApi;
